import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Biotech as BiotechIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon
} from "@mui/icons-material";
import reportData from "./lisBreastCancerReport.json";

const BreastCancerLISCard: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: "100%", margin: "0 auto" }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }} variant="outlined">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            {reportData.lis_report.metadata.laboratory_name}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Pathology Report
          </Typography>
          <Chip
            label={`Report ID: ${reportData.lis_report.metadata.report_id}`}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Report Date: {new Date(reportData.lis_report.metadata.report_date).toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pathologist: {reportData.lis_report.metadata.reporting_pathologist}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Accession #: {reportData.lis_report.metadata.accession_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Received: {new Date(reportData.lis_report.metadata.specimen_received_date).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Patient Information */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Patient Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.patient.patient_id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1" gutterBottom>
                {reportData.lis_report.patient.first_name} {reportData.lis_report.patient.last_name}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(reportData.lis_report.patient.date_of_birth).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">MRN</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.patient.medical_record_number}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Ordering Physician</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.patient.ordering_physician}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Specimen Details */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ScienceIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Specimen Details</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Specimen ID</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.specimen_id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Type</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.specimen_type}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Laterality</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.laterality}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Collection Date</Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(reportData.lis_report.specimen.collection_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Fixative</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.fixative}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Location</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.location}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary">Gross Description</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.specimen.gross_description}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Diagnosis */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AssignmentIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Diagnosis</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>Primary Diagnosis</Typography>
            <Typography variant="body1" paragraph>
              {reportData.lis_report.histopathology.diagnosis.primary}
            </Typography>
          </Paper>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Histologic Type</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.histopathology.diagnosis.histologic_type}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Tumor Size</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.histopathology.diagnosis.tumor_size}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Tumor Extension</Typography>
              <Typography variant="body1" gutterBottom>{reportData.lis_report.histopathology.diagnosis.tumor_extension}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Lymphovascular Invasion</Typography>
              <Chip
                label={reportData.lis_report.histopathology.diagnosis.lymphovascular_invasion}
                size="small"
                color={reportData.lis_report.histopathology.diagnosis.lymphovascular_invasion.includes('present') ? 'primary' : 'default'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Necrosis</Typography>
              <Chip
                label={reportData.lis_report.histopathology.diagnosis.necrosis}
                size="small"
                color={reportData.lis_report.histopathology.diagnosis.necrosis === 'Absent' ? 'success' : 'primary'}
              />
            </Grid>
          </Grid>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }} fontWeight="bold">Additional Findings</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2"><strong>DCIS Component:</strong> {reportData.lis_report.histopathology.additional_findings.dcis_component}</Typography>
              {reportData.lis_report.histopathology.additional_findings.dcis_component === 'Present' && (
                <>
                  <Typography variant="body2"><strong>DCIS Size:</strong> {reportData.lis_report.histopathology.additional_findings.dcis_size}</Typography>
                  <Typography variant="body2"><strong>DCIS Necrosis:</strong> {reportData.lis_report.histopathology.additional_findings.dcis_necrosis}</Typography>
                </>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2"><strong>Calcifications:</strong> {reportData.lis_report.histopathology.additional_findings.calcifications}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Immunohistochemistry */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <BiotechIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Immunohistochemistry</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">ER Status</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.immunohistochemistry.er.status}
                    size="small"
                    color={reportData.lis_report.immunohistochemistry.er.status === 'Positive' ? 'primary' : 'default'}
                  />
                </Box>
                <Typography variant="body2"><strong>Percentage:</strong> {reportData.lis_report.immunohistochemistry.er.percentage}</Typography>
                <Typography variant="body2"><strong>Intensity:</strong> {reportData.lis_report.immunohistochemistry.er.intensity}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">PR Status</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.immunohistochemistry.pr.status}
                    size="small"
                    color={reportData.lis_report.immunohistochemistry.pr.status === 'Positive' ? 'primary' : 'default'}
                  />
                </Box>
                <Typography variant="body2"><strong>Percentage:</strong> {reportData.lis_report.immunohistochemistry.pr.percentage}</Typography>
                <Typography variant="body2"><strong>Intensity:</strong> {reportData.lis_report.immunohistochemistry.pr.intensity}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">HER2 Status</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.immunohistochemistry.her2.status}
                    size="small"
                    color={reportData.lis_report.immunohistochemistry.her2.status === 'Positive' ? 'primary' : 'default'}
                  />
                </Box>
                <Typography variant="body2"><strong>IHC Score:</strong> {reportData.lis_report.immunohistochemistry.her2.ihc_score}</Typography>
                {reportData.lis_report.immunohistochemistry.her2.fish_ordered && (
                  <Typography variant="body2"><strong>FISH Ordered:</strong> Yes</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Ki67</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Percentage:</strong> {reportData.lis_report.immunohistochemistry.ki67.percentage}</Typography>
                <Typography variant="body2"><strong>Interpretation:</strong> {reportData.lis_report.immunohistochemistry.ki67.interpretation}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Additional Markers</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>p53:</strong> {reportData.lis_report.immunohistochemistry.additional_markers.p53}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>CK5/6:</strong> {reportData.lis_report.immunohistochemistry.additional_markers['ck5/6']}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>EGFR:</strong> {reportData.lis_report.immunohistochemistry.additional_markers.egfr}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Molecular Testing */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <DescriptionIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Molecular Testing</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">MammaPrint</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.molecular_testing.mammaprint.performed ? 'Performed' : 'Not Performed'}
                    size="small"
                    color={reportData.lis_report.molecular_testing.mammaprint.performed ? 'primary' : 'default'}
                  />
                </Box>
                {reportData.lis_report.molecular_testing.mammaprint.performed && (
                  <>
                    <Typography variant="body2"><strong>Result:</strong> {reportData.lis_report.molecular_testing.mammaprint.result}</Typography>
                    <Typography variant="body2"><strong>Score:</strong> {reportData.lis_report.molecular_testing.mammaprint.score}</Typography>
                    <Typography variant="body2"><strong>Interpretation:</strong> {reportData.lis_report.molecular_testing.mammaprint.interpretation}</Typography>
                  </>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Oncotype DX</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.molecular_testing.oncotype_dx.performed ? 'Performed' : 'Not Performed'}
                    size="small"
                    color={reportData.lis_report.molecular_testing.oncotype_dx.performed ? 'primary' : 'default'}
                  />
                </Box>
                {reportData.lis_report.molecular_testing.oncotype_dx.performed && (
                  <>
                    <Typography variant="body2"><strong>Recurrence Score:</strong> {reportData.lis_report.molecular_testing.oncotype_dx.recurrence_score}</Typography>
                    <Typography variant="body2"><strong>Interpretation:</strong> {reportData.lis_report.molecular_testing.oncotype_dx.interpretation}</Typography>
                    <Typography variant="body2"><strong>Distant Recurrence Risk:</strong> {reportData.lis_report.molecular_testing.oncotype_dx.distal_recurrence_risk}</Typography>
                  </>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">BRCA Testing</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={reportData.lis_report.molecular_testing.brca_testing.performed ? 'Performed' : 'Not Performed'}
                    size="small"
                    color={reportData.lis_report.molecular_testing.brca_testing.performed ? 'primary' : 'default'}
                  />
                </Box>
                {!reportData.lis_report.molecular_testing.brca_testing.performed && (
                  <Typography variant="body2"><strong>Reason:</strong> {reportData.lis_report.molecular_testing.brca_testing.reason}</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Microscopic Description & Comments */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <VerifiedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Pathologist Notes</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Microscopic Description</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">{reportData.lis_report.microscopic_description}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Comments</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <List dense>
                  {reportData.lis_report.comments.map((comment, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={comment} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Quality Control</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Case Reviewed By:</strong> {reportData.lis_report.quality_control.case_reviewed_by}</Typography>
                <Typography variant="body2"><strong>Review Date:</strong> {new Date(reportData.lis_report.quality_control.review_date).toLocaleDateString()}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default BreastCancerLISCard;